
// HelpAccordion.tsx
import React, { useEffect } from 'react';
import Accordion from 'react-bootstrap/Accordion';

interface HelpAccordionProps {
  activeKey: string;
  setActiveKey: (key: string) => void;
  items: any[];
  isAllQuestionsOpen: boolean;
  isSearching: boolean;
  currentUser: number;
}

const HelpAccordion: React.FC<HelpAccordionProps> = ({
  activeKey,
  setActiveKey,
  items,
  isAllQuestionsOpen,
  isSearching,
  currentUser
}) => {
  const routeToKey = (route: string) => route.replace("/", "");

  const renderHelpItem = (dynamicObject: any): JSX.Element | string | undefined => {
    if (typeof dynamicObject === 'object') return renderHelpItemObject(dynamicObject);
    if (typeof dynamicObject === 'string') return dynamicObject;
  };

  const renderHelpItemObject = (dynamicObject: any): JSX.Element => {
    const keys = Object.keys(dynamicObject);
    return (
      <>
        {keys.map((key) => {
          const [tag, id, className = ''] = key.split('_');
          let value = dynamicObject[key];
          if (Array.isArray(value)) value = value[currentUser ? currentUser - 1 : 1];

          const elementKey = `${id}_${Math.floor(Math.random() * 10000)}`;

          switch (tag) {
            case 'p': return renderParagraph(value, className, elementKey);
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
              return renderHeader(parseInt(tag.replace('h', '')), value, className, elementKey);
            case 'ol': return renderOrderedList(value, className, elementKey);
            case 'ul': return renderUnorderedList(value, className, elementKey);
            case 'li': return renderListItem(value, className, elementKey);
            case 'a': return renderLink(value, className, elementKey);
            default: return <></>;
          }
        })}
      </>
    );
  };

  const renderParagraph = (content: string, className: string, key: string): JSX.Element => {
    const parts = content.split('*');
    return (
      <p key={`p_${key}`} className={className}>
        {parts.map((part, i) => (i % 2 === 0 ? <strong key={`${key}_${i}`}>{part}</strong> : part))}
      </p>
    );
  };

  const renderHeader = (number: number, content: string, className: string, key: string): JSX.Element =>
    React.createElement(`h${number}`, { key, className: `help-sidebar-body-container__header ${className}` }, content);

  const renderOrderedList = (content: any, className: string, key: string): JSX.Element =>
    <ol key={`ol_${key}`} className={className}>{renderHelpItem(content)}</ol>;

  const renderUnorderedList = (content: any, className: string, key: string): JSX.Element =>
    <ul key={`ul_${key}`} className={`help-sidebar-body-container__unordered-list ${className}`}>{renderHelpItem(content)}</ul>;

  const renderListItem = (content: any, className: string, key: string): JSX.Element => {
    if (typeof content === 'string') {
      const parts = content.split('*');
      return (
        <li key={`li_${key}`} className={className}>
          {parts.map((part, i) => (i % 2 === 0 ? <strong key={`${key}_${i}`}>{part}</strong> : part))}
        </li>
      );
    }
    return <li key={`li_${key}`} className={className}>{renderHelpItem(content)}</li>;
  };

  const renderLink = (content: any, className: string, key: string): JSX.Element =>
    <a key={`a_${key}`} className={className} target="_blank" href="https://liquidacionelectronica.fiservargentina.com/guiadelectura/" rel="noreferrer">
      {renderHelpItem(content)}
    </a>;
  
  const isEmpty = (value:any): boolean =>{
    return (Array.isArray(value) &&  value[currentUser ? currentUser - 1 : 2] === '')   
  }

  return (
    <Accordion activeKey={activeKey}>
      {items.map((item, index) =>
        ((!isAllQuestionsOpen && index > 3 && !isSearching) || isEmpty(item.id)) ?
         null 
         : (
          <Accordion.Item
            className="mt-4"
            key={routeToKey(item.route)}
            eventKey={routeToKey(item.route)}
            onClick={() => setActiveKey(routeToKey(item.route) === activeKey ? '' : routeToKey(item.route))}
          >
            <Accordion.Header>
              {Array.isArray(item.title) ? item.title[currentUser ? currentUser - 1 : 2] : item.title}
            </Accordion.Header>
            <Accordion.Body>{renderHelpItem(item.body)}</Accordion.Body>
          </Accordion.Item>
        )
      )}
    </Accordion>
  );
};

export default HelpAccordion;
